import type { ColumnDef } from "@tanstack/react-table";
import { memo, useCallback, useEffect, useState } from "react";

import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableContainer,
  useDataTableControllerWithSorting,
} from "@/shared/components/data-table";
import { Batch } from "@/services/backend/batches/batch/batch";
import {
  ListRequest,
  useListBatchesQuery,
} from "@/services/backend/batches/batch/service";
import { formatDate, getBatchExpiryStatus } from "@/shared/utilities/date";
import { de } from "date-fns/locale";
import { cn } from "@/shared/lib/utils";
import { growthMedia, sampleTypes } from "@/shared/components/enum-labels";
import { PaginationLimitOffset } from "@/shared/components/ui/pagination-limit-offset";
import { ResetFilters } from "@/routes/processes/_components/reset-filters";
import { ColumnSelection } from "@/shared/components/ui/column-selection-component";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import { SearchInput } from "@/shared/components/domain/search/search-input";
import { FilterMode } from "@/shared/lib/filter/filter";
import { GrowthMediumFilter } from "./growth-medium-filter";
import { SampleTypeFilter } from "./sample-type-filter";
import EditBatchDialog from "./edit-batch-dialog";
import DeleteBatchButton from "./delete-batch-button";

function useSearchDebounce(
  value: string,
  delay?: number,
  minLength = 3,
): string {
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (value.length >= minLength) setSearch(value);
    else setSearch("");
  }, [value, minLength]);
  return useDebounce(search, delay);
}

const tableColumns: ColumnDef<Batch>[] = [
  {
    id: "batchNumber",
    accessorKey: "batchNumber",
    header: ({ column }) => (
      <Button
        className="p-0"
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Chargennummer
        {column.getIsSorted() === "asc" && (
          <ArrowUpIcon className="ml-2 h-4 w-4" />
        )}
        {column.getIsSorted() === "desc" && (
          <ArrowDownIcon className="ml-2 h-4 w-4" />
        )}
      </Button>
    ),
    cell: ({ row }) => <div>{row.original.batchNumber}</div>,
  },
  {
    id: "growthMedium",
    accessorKey: "growthMedium",
    header: "Nährmedium",
    cell: ({ row }) => <div>{growthMedia[row.original.growthMedium]}</div>,
  },
  {
    id: "sampleType",
    accessorKey: "sampleType",
    header: "Probentyp",
    cell: ({ row }) => <div>{sampleTypes[row.original.sampleType]}</div>,
  },
  {
    id: "expiryDate",
    accessorKey: "expiryDate",
    header: ({ column }) => (
      <Button
        className="p-0"
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Ablaufdatum
        {column.getIsSorted() === "asc" && (
          <ArrowUpIcon className="ml-2 h-4 w-4" />
        )}
        {column.getIsSorted() === "desc" && (
          <ArrowDownIcon className="ml-2 h-4 w-4" />
        )}
      </Button>
    ),
    cell: ({ row }) => {
      const status = getBatchExpiryStatus(row.original.expiryDate);
      return (
        <div
          className={cn(
            status === "expired" && "font-medium text-red-600",
            status === "expiring" && "font-medium text-amber-600",
          )}
        >
          {formatDate(row.original.expiryDate, de)}
        </div>
      );
    },
  },
  {
    id: "Optionen",
    accessorKey: "id",
    header: "Optionen",
    cell: ({ row }) => (
      <div className="flex flex-row items-center space-x-2">
        <EditBatchDialog batch={row.original} />
        <DeleteBatchButton batchId={row.original.id} />
      </div>
    ),
  },
];

const defaultRequest = (): ListRequest => ({
  batchNumber: { active: true, values: [] },
  growthMedium: { active: true, values: [] },
  sampleType: { active: true, values: [] },
  orderBy: [{ field: "expiryDate", dsc: true }],
  limit: 10,
  offset: 0,
});

const Toolbar = memo(function ToolbarComponent({
  search,
  onSearch,
  growthMedium,
  onGrowthMedium,
  sampleType,
  onSampleType,
  onReset,
}: {
  search: string;
  onSearch: (v: string) => void;
  growthMedium: NonNullable<ListRequest["growthMedium"]>;
  onGrowthMedium: (v: NonNullable<ListRequest["growthMedium"]>) => void;
  sampleType: NonNullable<ListRequest["sampleType"]>;
  onSampleType: (v: NonNullable<ListRequest["sampleType"]>) => void;
  onReset: () => void;
}) {
  return (
    <div className="flex justify-between space-x-2">
      <div className="flex space-x-2">
        <GrowthMediumFilter filter={growthMedium} onChange={onGrowthMedium} />
        <SampleTypeFilter filter={sampleType} onChange={onSampleType} />
        <ResetFilters onClick={onReset} />
      </div>
      <div className="flex space-x-2">
        <SearchInput value={search} onChange={onSearch} />
        {/* ColumnSelection lives with the table below */}
      </div>
    </div>
  );
});

function DataArea({
  query,
  growthMedium,
  sampleType,
}: {
  query: string;
  growthMedium: NonNullable<ListRequest["growthMedium"]>;
  sampleType: NonNullable<ListRequest["sampleType"]>;
}) {
  const [request, setRequest] = useState<ListRequest>(defaultRequest());

  useEffect(() => {
    setRequest((prev) => {
      const nextBatchNumber = {
        active: true,
        mode: FilterMode.Like as const,
        values: [`%${query}%`],
      };
      const same =
        prev.batchNumber?.active === nextBatchNumber.active &&
        prev.batchNumber?.mode === nextBatchNumber.mode &&
        (prev.batchNumber?.values?.[0] ?? "") === nextBatchNumber.values[0];
      return same ? prev : { ...prev, batchNumber: nextBatchNumber, offset: 0 };
    });
  }, [query]);

  useEffect(() => {
    setRequest((prev) => {
      const gmSame =
        JSON.stringify(prev.growthMedium) === JSON.stringify(growthMedium);
      const stSame =
        JSON.stringify(prev.sampleType) === JSON.stringify(sampleType);
      if (gmSame && stSame) return prev;
      return { ...prev, growthMedium, sampleType, offset: 0 };
    });
  }, [growthMedium, sampleType]);

  const {
    data: list,
    isFetching,
    isLoading,
    error,
  } = useListBatchesQuery(request, {
    refetchOnMountOrArgChange: true,
  });

  const { table, sorting } = useDataTableControllerWithSorting(
    tableColumns,
    list?.items || [],
    [{ id: "expiryDate", desc: true }],
  );

  useEffect(() => {
    setRequest((prev) => {
      const orderBy = sorting.map((s) => ({ field: s.id, dsc: s.desc }));
      const same =
        prev.orderBy?.length === orderBy.length &&
        prev.orderBy?.every(
          (o, i) => o.field === orderBy[i].field && o.dsc === orderBy[i].dsc,
        );
      return same ? prev : { ...prev, orderBy, offset: 0 };
    });
  }, [sorting]);

  if (error) return <RTKQueryErrorAlert error={error} />;

  return (
    <>
      <DataTableContainer className="relative">
        <DataTable
          table={table}
          columns={tableColumns}
          isLoading={isLoading && !list}
        />
        {isFetching && list && (
          <div className="bg-background/60 pointer-events-none absolute inset-0 flex items-center justify-center backdrop-blur-sm">
            <span className="text-sm opacity-70">Laden…</span>
          </div>
        )}
      </DataTableContainer>

      <div className="mt-2 flex items-center justify-between">
        <ColumnSelection table={table} />
        {list && (
          <PaginationLimitOffset
            offset={request.offset!}
            limit={request.limit!}
            totalCount={list.count}
            setLimitOffset={(limit, offset) =>
              setRequest((prev) => ({ ...prev, limit, offset }))
            }
          />
        )}
      </div>
    </>
  );
}

export function BatchListTable() {
  // Toolbar-local state
  const [search, setSearch] = useState("");
  const [growthMedium, setGrowthMedium] = useState<
    NonNullable<ListRequest["growthMedium"]>
  >({
    active: true,
    values: [],
  });
  const [sampleType, setSampleType] = useState<
    NonNullable<ListRequest["sampleType"]>
  >({
    active: true,
    values: [],
  });

  const query = useSearchDebounce(search, 400, 0);

  const handleReset = useCallback(() => {
    setSearch("");
    setGrowthMedium({ active: true, values: [] });
    setSampleType({ active: true, values: [] });
  }, []);

  const handleGrowthMedium = useCallback(
    (v: NonNullable<ListRequest["growthMedium"]>) => setGrowthMedium(v),
    [],
  );
  const handleSampleType = useCallback(
    (v: NonNullable<ListRequest["sampleType"]>) => setSampleType(v),
    [],
  );

  return (
    <div className="flex w-full flex-col space-y-2">
      <Toolbar
        search={search}
        onSearch={setSearch}
        growthMedium={growthMedium}
        onGrowthMedium={handleGrowthMedium}
        sampleType={sampleType}
        onSampleType={handleSampleType}
        onReset={handleReset}
      />

      <DataArea
        query={query}
        growthMedium={growthMedium}
        sampleType={sampleType}
      />
    </div>
  );
}
